
import bugsnagClient from 'JS/global/bugsnag';
import DetailsStep from './steps/DetailsStep.vue';
import Icon from 'Src/js/components/Icon.vue';
import SavedVehicleMetadata from 'JS/components/automotive/SavedVehicleMetadata.vue';
import StepsProgress from './steps/StepsProgress.vue';
import TireSizeStep from './steps/TireSizeStep.vue';
import TrimStep from './steps/TrimStep.vue';
import VehicleStep from './steps/VehicleStep.vue';
import Vue from 'vue';
import { Component, Provide } from 'vue-property-decorator';
import { createDetailsStepData, createTireSizeStepData, createTrimStepData, createVehicleStepData } from './data/steps';
import { CustomerVehicleIconColor, ICustomerVehicle } from 'JS/types/Automotive';
import { CustomerVehicleService } from 'JS/services/automotive/CustomerVehicleService';
import { FilterKeysByValueType } from 'Src/js/types/utility';
import { DetailsStep as IDetailsStep, TireSizeStep as ITireSizeStep, TrimStep as ITrimStep, VehicleStep as IVehicleStep, VehicleLookupStepType } from './types/steps';
import { VehicleLookupService } from 'JS/services/automotive/VehicleLookupService';

interface Steps {
	vehicle: IVehicleStep;
	trim: ITrimStep;
	tireSize: ITireSizeStep;
	details: IDetailsStep;
}

type StepName = keyof Steps;

@Component({
	name: 'VehicleLookup',
	components: {
		Icon,
		StepsProgress,
		VehicleStep,
		TrimStep,
		TireSizeStep,
		DetailsStep,
		SavedVehicleMetadata,
	}
})
class VehicleLookup extends Vue {

	@Provide('VehicleLookupService')
	vehicleLookupService = new VehicleLookupService();

	currentStep: StepName = 'vehicle';
	customerVehicleService = new CustomerVehicleService();
	loading: boolean = false;
	steps: Steps = {
		vehicle: {
			type: VehicleLookupStepType.Vehicle,
			complete: false,
			dependsOnPreviousStep: false,
			data: stepDataFactory('vehicle'),
		},
		trim: {
			type: VehicleLookupStepType.Trim,
			complete: false,
			dependsOnPreviousStep: true,
			data: stepDataFactory('trim'),
		},
		tireSize: {
			type: VehicleLookupStepType.TireSize,
			complete: false,
			dependsOnPreviousStep: true,
			required: false,
			data: stepDataFactory('tireSize'),
		},
		details: {
			type: VehicleLookupStepType.Details,
			dependsOnPreviousStep: false,
			complete: false,
			data: stepDataFactory('details'),
		},
	}
	started = false;

	get stepNames(): StepName[] {
		return Object.keys(this.steps) as StepName[];
	}

	get stepProgress() {
		const currentStepIndex = this.stepNames.indexOf(this.currentStep);

		return {
			steps: this.stepNames.length,
			currentStepIndex,
			currentStep: currentStepIndex + 1,
		};
	}

	get vehicleInfo() {
		if (this.currentStep === 'vehicle') {
			return null;
		}

		const { vehicle: { data: vehicleData }, trim, tireSize } = this.steps;

		const vehicleInfo: { year: string, make: string, model: string, trim?: string, tireSize?: string } = {
			year: vehicleData.year,
			make: vehicleData.make,
			model: vehicleData.model,
		};

		if (this.stepProgress.currentStepIndex > this.stepNames.indexOf('trim') && typeof trim.data.trim === 'string') {
			vehicleInfo.trim = trim.data.trim;
		}

		if (this.stepProgress.currentStepIndex > this.stepNames.indexOf('tireSize')) {
			vehicleInfo.tireSize = tireSize.data.size;
		}

		return vehicleInfo;
	}

	get customerVehicle(): ICustomerVehicle {
		const { vehicle, trim, tireSize, details } = this.steps;

		const customerVehicle: ICustomerVehicle = {
			year: vehicle.data.year,
			make: vehicle.data.make,
			model: vehicle.data.model,
			color: details.data.color as CustomerVehicleIconColor,
			name: details.data.nickname,
			iconType: details.data.icon,
			tireSizeDescription: tireSize.data.size,
		};

		if (typeof trim.data.trim === 'string' && trim.data.trim.length) {
			customerVehicle.trim = trim.data.trim;
		}

		return customerVehicle;
	}

	continueToNextStep() {
		this.steps[this.currentStep].complete = true;

		switch (this.currentStep) {
			case 'vehicle': {
				this.sendGtmEvent('tire_vehicle');
				this.currentStep = 'trim';
				break;
			}
			case 'trim': {
				this.sendGtmEvent('tire_vehicle_trim');
				this.currentStep = 'tireSize';
				break;
			}
			case 'tireSize': {
				this.sendGtmEvent('tire_size');
				this.currentStep = 'details';
				break;
			}
			case 'details': {
				this.sendGtmEvent('save_vehicle');
				this.$nextTick(() => {
					this.saveVehicle();
				});
				break;
			}
			default:
				break;
		}
	}

	goToPreviousStep() {
		switch (this.currentStep) {
			case 'vehicle': {
				break;
			}
			case 'trim': {
				this.currentStep = 'vehicle';
				break;
			}
			case 'tireSize': {
				this.currentStep = 'trim';
				break;
			}
			case 'details': {
				this.currentStep = 'tireSize';
				break;
			}
			default:
				break;
		}
	}

	clearFollowingSteps(stepName: StepName) {
		this.steps[stepName].complete = false;

		const index = this.stepNames.indexOf(stepName);
		const namesOfFollowingSteps = this.stepNames.slice(index + 1);

		namesOfFollowingSteps.forEach(name => {
			const step = this.steps[name];
			if (step.dependsOnPreviousStep) {
				type DependentStepName = keyof FilterKeysByValueType<Steps, { dependsOnPreviousStep: true }>;

				step.complete = false;
				step.data = stepDataFactory(name as DependentStepName);
			}
		});
	}

	sendGtmEvent(eventName: String) {
		window.blainGtmDataLayer.push({
			'automotiveEvent': eventName,
			'event': "automotive_event"
		});
	}

	async saveVehicle() {
		try {
			this.loading = true;
			const vehicle = await this.customerVehicleService.addVehicle(this.customerVehicle);
			this.$emit('savedVehicle', vehicle);
		} catch (e) {
			bugsnagClient.notify(e, {
				metaData: {
					info: "Failed to save customer vehicle"
				}
			});
		} finally {
			this.loading = false;
		}
	}

	mounted() {
		this.$nextTick(() => {
			this.started = true;
		});
	}
}

export default VehicleLookup;

function stepDataFactory<K extends keyof Steps>(name: K): Steps[K]['data'] {
	switch (name) {
		case 'vehicle':
			return createVehicleStepData();
		case 'trim':
			return createTrimStepData();
		case 'tireSize':
			return createTireSizeStepData();
		case 'details':
			return createDetailsStepData();
		default:
			break;
	}
}
